"use client";
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { OUT_OF_STORE_LIMIT, tryOnSizeDec } from "@/config/constConf";
import { jointImg, showPrice, tranColorByName } from "@/lib/commonService";
import { filterByLensTotalWidth } from "@/src/utils/lens/productdetail";

import {
    gaCommonEvent,
    getProductItemParams,
    productAddToCar,
} from "@/src/utils/gtagEvents";
import { FmButton, FmDrawer, FmImage, FmLink } from "@/ui-component";
import stl from "./tryOnProductDec.module.scss";
import { useTranslation } from "@/src/i18n/client";
import variable from "@/src/styles/variables.module.scss";
import { useTryonStore } from "@/store/modules/tryon";
import { LensPageDiversion } from "../LoadAnalysis/analysis/abTest";
import { useCommonContext } from "@/src/provider";
import { snapProductAddToCar } from "@/src/utils/snapEvents";
import { create } from "@/src/models/basket";
import { addToCartClickEvent } from "@/src/models/emailData";
import criteoAd from "../LoadAnalysis/analysis/criteo";
import { useRouter } from "next/navigation";

interface Props {}

const TryOnProductDec: React.FC<Props> = (props) => {
    const {
        lensColor,
        tryOnProduct,
        productColorImg,
        activeColor,
        colorList,
        colorId,
    } = useTryonStore((state) => state);
    const { t } = useTranslation(["lens", "common", "notice", "productDetail"]);
    const [isShowModal, setIsShowModal] = useState(false);
    const { site } = useCommonContext();
    const router = useRouter();
    function showModal() {
        setIsShowModal(true);
    }

    function hideModal() {
        setIsShowModal(false);
    }

    function onSelectLens() {
        gaCommonEvent("select", {
            event_category: "try on",
        });
    }

    const getSizeDecInfo = useCallback(() => {
        const {
            dimensions_lens,
            dimensions_bridge,
            dimensions_temple,
            dimensions_total_width,
            dimensions_diagonal,
            products_weight,
            products_metal,
        } = tryOnProduct;
        const width = filterByLensTotalWidth(dimensions_total_width);
        const widthText =
            width === "S"
                ? t("productDetail:SMALL")
                : width === "M"
                  ? t("productDetail:MEDIUM")
                  : t("productDetail:LARGE");
        return [
            {
                title: `${t("productDetail:LENS_WIDTH")}(mm)`,
                imgSrc: tryOnSizeDec["LensWidth"],
                content: (
                    <span>
                        <b style={{ color: variable.main }}>
                            {dimensions_lens}
                        </b>
                        -{dimensions_bridge}-{dimensions_temple}
                    </span>
                ),
            },
            {
                title: `${t("productDetail:BRIDGE_WIDTH")}(mm)`,
                imgSrc: tryOnSizeDec["BridgeWidth"],
                content: (
                    <span>
                        {dimensions_lens}-
                        <b style={{ color: variable.main }}>
                            {dimensions_bridge}
                        </b>
                        -{dimensions_temple}
                    </span>
                ),
            },
            {
                title: `${t("productDetail:TEMPLE_LENGTH")}(mm)`,
                imgSrc: tryOnSizeDec["TempleLength"],
                content: (
                    <span>
                        {dimensions_lens}-{dimensions_bridge}-
                        <b style={{ color: variable.main }}>
                            {dimensions_temple}
                        </b>
                    </span>
                ),
            },
            {
                title: `${t("productDetail:TotalWidth")}`,
                imgSrc: tryOnSizeDec["TotalWidth"],
                content: (
                    <span>
                        <b style={{ color: variable.main }}>
                            {dimensions_total_width}
                        </b>
                        mm (
                        <span style={{ textTransform: "lowercase" }}>
                            {widthText}
                        </span>
                        )
                    </span>
                ),
            },
            {
                title: `${t("productDetail:eye")}`,
                imgSrc: tryOnSizeDec["LensDiagonalSize"],
                content: (
                    <span>
                        <b style={{ color: variable.main }}>
                            {dimensions_diagonal}
                        </b>
                        mm
                    </span>
                ),
            },
            {
                title: `${t("productDetail:Weight")}`,
                imgSrc: tryOnSizeDec["Weight"],
                content: (
                    <span>
                        <b style={{ color: variable.main }}>
                            {products_weight}
                        </b>
                        g
                    </span>
                ),
            },
            {
                title: `${t("productDetail:Material")}`,
                imgSrc: tryOnSizeDec["Material"],
                content: (
                    <span>
                        <b style={{ color: variable.main }}>{products_metal}</b>
                    </span>
                ),
            },
        ];
    }, [t, tryOnProduct]);

    const imgShow = useMemo(
        () =>
            jointImg({
                productId: tryOnProduct.products_id,
                size: "middle",
                imgTotal: 2,
                name: activeColor.color_image,
            }),
        [activeColor.color_image, tryOnProduct.products_id],
    );

    const decList = useMemo(() => getSizeDecInfo(), [getSizeDecInfo]);

    const colorLabel = useMemo(() => {
        let tempColorLabel = "";
        const _colorNameList = (activeColor.color_name || "").split(",");
        for (let i = 0; i < _colorNameList.length; i++) {
            if (i !== 0) {
                tempColorLabel += ",";
            }
            tempColorLabel += tranColorByName({
                t,
                color_name: _colorNameList[i],
            });
        }

        return tempColorLabel;
    }, [t, activeColor.color_name]);

    const isSelectLensDisable = useMemo(() => {
        return (
            (activeColor.color_qty &&
                activeColor.color_qty < OUT_OF_STORE_LIMIT) ||
            tryOnProduct.products_status == 0 ||
            activeColor.color_status == 0
        );
    }, [
        activeColor.color_qty,
        activeColor.color_status,
        tryOnProduct.products_status,
    ]);

    // 是否是成品
    const isFinishedProduct = useMemo(
        () => tryOnProduct.products_type == 2,
        [tryOnProduct.products_type],
    );

    const handleButtonClick = useCallback(() => {
        gaCommonEvent("more info", {
            event_category: "try on",
        });
        showModal();
    }, []);

    const decListDom = useMemo(() => {
        return decList.map((arg: any, index: number) => (
            <div key={arg.title} className={stl["product-dec-item"]}>
                <div className={stl["product-dec-item-image"]}>
                    <FmImage
                        image={arg.imgSrc}
                        // style={{ width: "80%" }}
                        width={101}
                        height={43}
                    />
                </div>
                <p>{arg.title}</p>
                <div>{arg.content}</div>
            </div>
        ));
    }, [decList]);

    const productColorImgDom = useMemo<ReactNode>(() => {
        return productColorImg ? (
            <FmImage
                className={stl["product-color-img"]}
                image={productColorImg}
                alt=""
                width={159}
                height={80}
            />
        ) : (
            <FmImage image={imgShow} width={159} height={80} />
        );
    }, [imgShow, productColorImg]);

    const lensColorTitleDom = useMemo(() => {
        return (
            lensColor.title &&
            lensColor.title != "Clear" && (
                <p>
                    <span>{t("lens:LensTint")}:</span>
                    <br />
                    <span>{t(`lens:${lensColor.title}`)}</span>
                </p>
            )
        );
    }, [lensColor.title, t]);

    const getGAStatistics = useCallback(() => {
        //根据 activeColorId 获取商品colorindex
        const showColorIdx = colorList.findIndex(
            (item: any) => item.color_id == colorId,
        );
        //GA商品详情统计
        const pathName = window.location.pathname;
        return getProductItemParams({
            productList: [tryOnProduct],
            colorIndex: showColorIdx !== -1 ? showColorIdx : 0,
            category: `pc_${pathName}`,
            list_name: `pc_${pathName}`,
        });
    }, [colorId, colorList, tryOnProduct]);
    //添加购物车
    const addCartGA = useCallback(() => {
        const sendData = getGAStatistics();
        sendData.length &&
            productAddToCar({
                items: sendData,
            });
        sendData.length &&
            snapProductAddToCar({
                items: sendData,
            });
    }, [getGAStatistics]);
    // 成品直接加购
    const onAddCart = useCallback(() => {
        const data = {
            product_id: tryOnProduct.products_id,
            color_id: colorId,
            cart_quantity: 1,
            "id[2]": 300,
        };
        const price = Number(
            activeColor.product_price
                ? showPrice(activeColor.product_price).price
                : showPrice(activeColor).price,
        );
        create(data)
            .then(() => {
                addToCartClickEvent({
                    colorId: colorId,
                    price: price,
                });
                criteoAd.addToCart({
                    id: colorId,
                    price: price,
                    quantity: 1,
                });
                router.push("/basket");
            })
            .catch((e) => console.log(e));

        try {
            addCartGA();
        } catch (e) {}
    }, [activeColor, addCartGA, colorId, router, tryOnProduct.products_id]);

    const selectLensHref = useMemo(
        () =>
            `${
                LensPageDiversion({
                    site: site,
                    products_model: tryOnProduct.products_model,
                    master_categories_id: tryOnProduct.master_categories_id,
                })
                    ? "lens"
                    : "newlens"
            }?lensColor=${activeColor.color_id}&products_id=${tryOnProduct.products_id}`,
        [
            activeColor.color_id,
            site,
            tryOnProduct.master_categories_id,
            tryOnProduct.products_id,
            tryOnProduct.products_model,
        ],
    );

    const isSelectLensDisableDom = useMemo(() => {
        return !isSelectLensDisable ? (
            !isFinishedProduct ? (
                <FmLink href={selectLensHref} target={"_blank"}>
                    <FmButton
                        disabled={activeColor.color_qty < 1}
                        onClick={onSelectLens}
                        style={{
                            width: "3.6rem",
                        }}
                    >
                        {t("productDetail:SelectLenses")}
                    </FmButton>
                </FmLink>
            ) : (
                <FmButton
                    disabled={activeColor.color_qty < 1}
                    onClick={onAddCart}
                    style={{
                        width: "3.6rem",
                    }}
                >
                    {t("common:ADD_TO_CART")}
                </FmButton>
            )
        ) : (
            <FmButton disabled={true}>{t("productDetail:SOLD_OUT")}</FmButton>
        );
    }, [
        isSelectLensDisable,
        isFinishedProduct,
        selectLensHref,
        activeColor.color_qty,
        t,
        onAddCart,
    ]);

    const showModalChildren = useMemo(() => {
        return (
            <>
                <div className={stl["try-on-detail-img-container"]}>
                    <div className={stl["try-on-detail-img"]}>
                        {productColorImgDom}
                    </div>
                    <div className={stl["try-on-detail-text"]}>
                        <p className={stl["try-on-details-p-title"]}>
                            {tryOnProduct.products_name_attribute ||
                                tryOnProduct.products_model}
                        </p>
                        <p className={stl["try-on-details-p"]}>
                            <span>{t("lens:FrameColor")}:</span>
                            <br />
                            <span>{`${colorLabel}${
                                activeColor.color_number
                                    ? `(${activeColor.color_number})`
                                    : ""
                            }`}</span>
                        </p>

                        {lensColorTitleDom}
                    </div>
                </div>
                <div className={stl["mention-tip"]}>
                    {t("common:TryOn.MenTionTip")}
                </div>

                <div className={stl["frame-details"]}>
                    {`${t("common:TryOn.FrameDetails")}:`}
                </div>
                <div className={stl["dec-list-container"]}>{decListDom}</div>
                <div className={stl["select-lens-container"]}>
                    <div className={stl["select-lens-content"]}>
                        {isSelectLensDisableDom}
                    </div>
                </div>
            </>
        );
    }, [
        colorLabel,
        decListDom,
        isSelectLensDisableDom,
        lensColorTitleDom,
        productColorImgDom,
        t,
        activeColor.color_number,
        tryOnProduct.products_model,
        tryOnProduct.products_name_attribute,
    ]);
    const showModalDom = useMemo(() => {
        return (
            <FmDrawer
                open={isShowModal}
                onClose={hideModal}
                closeable={true}
                anchor={"bottom"}
                fullHeight
                closeablePosition="right"
                headerChildren={t(
                    "notice:tryOn3D.PLEASE_CONFIRM_THE_FRAME_DETAILS",
                )}
            >
                {showModalChildren}
            </FmDrawer>
        );
    }, [isShowModal, showModalChildren, t]);
    return (
        <>
            <FmButton
                style={{
                    marginLeft: "0.4rem",
                }}
                onClick={handleButtonClick}
                className={stl["more-info-button"]}
            >
                {t("notice:tryOn3D.MORE_INFO")}
            </FmButton>
            {showModalDom}
        </>
    );
};

export default TryOnProductDec;
